/**
 * Consentimiento de cookies RGPD/AEPD.
 * - Técnicas: siempre activas (necesarias para el sitio).
 * - Analíticas: solo si el usuario acepta; controla la carga de Google Analytics.
 */
import { createContext, useContext, useCallback, useState, useEffect } from "react";
import { init as initAnalytics } from "../utils/analytics";

const STORAGE_KEY = "guia_cookie_consent";
const CONSENT_VERSION = 1;

const defaultConsent = () => ({
	necessary: true,
	analytics: false,
	version: CONSENT_VERSION,
});

function readConsent() {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (parsed && typeof parsed.analytics === "boolean" && parsed.version === CONSENT_VERSION) {
			return { ...defaultConsent(), ...parsed };
		}
	} catch (_) {}
	return null;
}

function writeConsent(consent) {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, version: CONSENT_VERSION }));
	} catch (_) {}
}

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
	const [consent, setConsentState] = useState(null);
	const [preferencesOpen, setPreferencesOpen] = useState(false);

	// Cargar consentimiento guardado al montar (sin bloquear la UI)
	useEffect(() => {
		const saved = readConsent();
		if (saved) {
			setConsentState(saved);
			if (saved.analytics) initAnalytics();
		}
	}, []);

	const setConsent = useCallback((value) => {
		setConsentState(value);
		if (value) writeConsent(value);
		if (value?.analytics) initAnalytics();
	}, []);

	const acceptAll = useCallback(() => {
		const next = defaultConsent();
		next.analytics = true;
		setConsent(next);
		setPreferencesOpen(false);
	}, [setConsent]);

	const rejectAll = useCallback(() => {
		const next = defaultConsent();
		next.analytics = false;
		setConsent(next);
		setPreferencesOpen(false);
	}, [setConsent]);

	const savePreferences = useCallback(
		(analytics) => {
			const next = defaultConsent();
			next.analytics = Boolean(analytics);
			setConsent(next);
			setPreferencesOpen(false);
		},
		[setConsent]
	);

	const openPreferences = useCallback(() => setPreferencesOpen(true), []);
	const closePreferences = useCallback(() => setPreferencesOpen(false), []);

	const value = {
		consent,
		hasConsent: consent !== null,
		analyticsEnabled: consent?.analytics === true,
		acceptAll,
		rejectAll,
		savePreferences,
		preferencesOpen,
		openPreferences,
		closePreferences,
	};

	return (
		<CookieConsentContext.Provider value={value}>
			{children}
		</CookieConsentContext.Provider>
	);
}

export function useCookieConsent() {
	const ctx = useContext(CookieConsentContext);
	if (!ctx) throw new Error("useCookieConsent must be used within CookieConsentProvider");
	return ctx;
}
